<div class="flex h-screen flex-col bg-white">
  <div class="flex shrink-0 items-center justify-between border-b border-gray-200 px-6 py-4">
    <h2 class="text-lg font-semibold text-gray-800 dark:text-white/90">Reply to review</h2>
    <button
      mat-icon-button
      type="button"
      aria-label="Close"
      (click)="close()"
      [disabled]="savingTemplate() || deletingTemplate() || submittingReply()">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <mat-dialog-content class="m-0 flex-1 overflow-hidden p-0">
    <mat-tab-group
      class="h-full"
      [selectedIndex]="activeTab === 'select' ? 0 : 1"
      (selectedIndexChange)="onTabChange($event)">
      <mat-tab label="Select template / Reply">
        <div class="grid h-full grid-cols-1 gap-6 overflow-y-auto p-6 lg:grid-cols-[380px_1fr] lg:overflow-hidden">
          <!-- Review context -->
          <div class="flex flex-col gap-3 overflow-y-auto lg:border-r lg:border-gray-200 lg:pr-6">
            <div class="rounded-lg border border-gray-200 p-4 text-sm text-gray-700">
              <div class="font-medium">Author: {{ review.authorName || 'Unknown author' }}</div>
              <div>Dealer: {{ review.dealerName || 'Unknown dealer' }}</div>
              <p class="mt-3 whitespace-pre-wrap text-gray-600">{{ review.response }}</p>
            </div>
          </div>

          <!-- Reply editor -->
          <div class="flex min-w-0 flex-col gap-4 overflow-y-auto lg:overflow-hidden">
            <div class="flex shrink-0 items-start gap-2 mt-2">
              <mat-form-field appearance="outline" class="flex-1">
                <mat-label>Template</mat-label>
                <mat-select
                  [value]="form.controls.templateId.value"
                  (selectionChange)="onTemplateSelection($event.value)">
                  <mat-option value="">Custom reply</mat-option>
                  @for (group of groupedTemplates; track group.title ?? $index) {
                    <mat-optgroup [label]="group.title">
                      @for (template of group.value; track template.id ?? template.name ?? $index) {
                        <mat-option [value]="template.id ?? ''">
                          {{ template.name || 'Untitled template' }}
                        </mat-option>
                      }
                    </mat-optgroup>
                  }
                </mat-select>
              </mat-form-field>

              <button
                mat-icon-button
                type="button"
                aria-label="New template"
                (click)="startCreatingTemplate()"
                [disabled]="savingTemplate() || deletingTemplate() || submittingReply()">
                <mat-icon>add</mat-icon>
              </button>

              @if (review) {
                <span
                  class="inline-flex"
                  [matTooltip]="hasReviewComment ? 'Let our AI do the job, just click an a response will be generated for you.' : 'AI generation is unavailable for reviews without a comment.'"
                  matTooltipPosition="above">
                  <button
                    mat-icon-button
                    type="button"
                    aria-label="Generate with AI"
                    (click)="generateWithAi()"
                    [disabled]="!hasReviewComment || savingTemplate() || deletingTemplate() || submittingReply() || generatingComment()">
                    <mat-icon>auto_awesome</mat-icon>
                  </button>
                </span>
              }
              @if (selectedTemplate) {
                <button
                  mat-icon-button
                  type="button"
                  aria-label="Edit template"
                  (click)="startEditingTemplate()"
                  [disabled]="!canEditSelectedTemplate || savingTemplate() || deletingTemplate() || submittingReply()">
                  <mat-icon>edit</mat-icon>
                </button>
              }
            </div>

            @if (loadingTemplates()) {
              <mat-progress-bar mode="indeterminate" class="shrink-0"></mat-progress-bar>
            }

            @if (templateError()) {
              <div class="shrink-0 text-sm text-red-600">{{ templateError() }}</div>
            }

            @if (generateCommentError()) {
              <div class="shrink-0 text-sm text-red-600">{{ generateCommentError() }}</div>
            }

            <mat-form-field appearance="outline" class="dialog-textarea-field min-h-0 flex-1">
              <mat-label>Reply</mat-label>
              <textarea
                matInput
                maxlength="5000"
                [formControl]="form.controls.reply"></textarea>
              <mat-hint align="end">{{ form.controls.reply.value.length }}/5000</mat-hint>
              @if (form.controls.reply.touched && form.controls.reply.hasError('required')) {
                <mat-error>Reply is required.</mat-error>
              }
            </mat-form-field>

            @if (submitError()) {
              <div class="shrink-0 text-sm text-red-600">{{ submitError() }}</div>
            }

            <div class="flex shrink-0 justify-end">
              <app-button
                variant="primary"
                (btnClick)="submitReply()"
                [disabled]="submittingReply() || savingTemplate() || deletingTemplate() || !form.controls.reply.value.trim()">
                Submit reply
              </app-button>
            </div>
          </div>
        </div>
      </mat-tab>

      <mat-tab label="Create / Edit template">
        <div class="grid h-full grid-cols-1 gap-6 overflow-y-auto p-6 lg:grid-cols-[380px_1fr] lg:overflow-hidden">
          <!-- Template picker -->
          <div class="flex flex-col gap-3 overflow-y-auto lg:border-r lg:border-gray-200 lg:pr-6">
            <div class="flex items-start gap-2 mt-2">
              <mat-form-field appearance="outline" class="flex-1">
                <mat-label>Template to edit</mat-label>
                <mat-select
                  [value]="form.controls.templateId.value"
                  (selectionChange)="onTemplateSelectionForManage($event.value)">
                  <mat-option value="">Create new template</mat-option>
                  @for (group of groupedTemplates; track group.title ?? $index) {
                    @if (group.title === ownGroupLabel) {
                      <mat-optgroup [label]="group.title">
                        @for (template of group.value; track template.id ?? template.name ?? $index) {
                          <mat-option [value]="template.id ?? ''">
                            {{ template.name || 'Untitled template' }}
                          </mat-option>
                        }
                      </mat-optgroup>
                    }
                  }
                </mat-select>
              </mat-form-field>

              @if (canManageSelectedTemplate) {
                <button
                  mat-icon-button
                  type="button"
                  aria-label="Delete template"
                  (click)="deleteTemplate()"
                  [disabled]="!canDeleteSelectedTemplate || savingTemplate() || deletingTemplate() || submittingReply()">
                  <mat-icon>delete</mat-icon>
                </button>
              }
            </div>

            @if (templateSuccessMessage) {
              <div class="text-sm text-emerald-700">{{ templateSuccessMessage }}</div>
            }
          </div>

          <!-- Template editor -->
          <div class="flex min-w-0 flex-col gap-4 overflow-y-auto lg:overflow-hidden mt-2">
            @if (templateError()) {
              <div class="shrink-0 text-sm text-red-600">{{ templateError() }}</div>
            }

            <mat-form-field appearance="outline" class="shrink-0">
              <mat-label>Template title</mat-label>
              <input matInput [formControl]="form.controls.title" maxlength="255" />
              @if (form.controls.title.touched && !form.controls.title.value.trim()) {
                <mat-error>Title is required to save a template.</mat-error>
              }
            </mat-form-field>

            <mat-form-field appearance="outline" class="dialog-textarea-field min-h-0 flex-1">
              <mat-label>Template content</mat-label>
              <textarea
                matInput
                maxlength="5000"
                [formControl]="form.controls.reply"></textarea>
              <mat-hint align="end">{{ form.controls.reply.value.length }}/5000</mat-hint>
              @if (form.controls.reply.touched && form.controls.reply.hasError('required')) {
                <mat-error>Reply is required.</mat-error>
              }
            </mat-form-field>

            <div class="flex shrink-0 justify-end gap-2">
              <app-button
                variant="primary"
                (btnClick)="saveTemplate()"
                [disabled]="savingTemplate() || deletingTemplate() || submittingReply()">
                {{ templateMode === 'edit' ? 'Update template' : 'Save template' }}
              </app-button>
              <app-button
                variant="outline"
                (btnClick)="startCreatingTemplate()"
                [disabled]="savingTemplate() || deletingTemplate() || submittingReply()">
                Clear &amp; create new
              </app-button>
            </div>
          </div>
        </div>
      </mat-tab>
    </mat-tab-group>
  </mat-dialog-content>
</div>
